<template>
  <BaseCard no-padding>
    <div class="tw:relative tw:bg-white tw:w-full tw:rounded-xl">
      <NotEligibleStepper />

      <div class="tw:pt-10 tw:pb-[60px] tw:px-6 tw:mx-auto tw:flex tw:flex-col tw:justify-center tw:md:w-[500px]">
        <div
          class="tw:bg-green-400 tw:p-3 tw:rounded-full tw:flex tw:self-center tw:mb-4"
          :class="selectedPartner !== null ? 'tw:bg-green-400' : 'tw:bg-red-600'"
        >
          <PartnerIcon />
        </div>
        <h3 class="tw:text-[28px] tw:leading-[35px] tw:text-center tw:font-bold tw:mb-4">
          {{ $t(title) }}
        </h3>
        <p v-if="selectedPartner === null" v-html="$t(subtitle)" />
        <p
          data-testid="readmore-eligible-message-text"
          v-else
          v-html="$t('rc_eligible_message', { ConnectivityProvider: selectedPartner })"
        />

        <div
          v-if="selectedPartner !== null"
          class="tw:mt-6 tw:shadow-lg tw:rounded-xl tw:p-2 tw:flex tw:md:flex-row tw:flex-col tw:justify-between tw:gap-y-4"
        >
          <div class="tw:px-2">
            <p class="tw:text-gray-600 tw:text-xs">
              {{ $t('rc_menu_booking_engine_selected') }}
            </p>
            <p data-testid="readmore-selected-engine-text" class="tw:font-bold">{{ selectedPartner }}</p>
          </div>
          <MainButton dataTestId="readmore-send-email-button" @click="openEmailClient">{{
            $t('rc_contact_cp_cta')
          }}</MainButton>
        </div>
        <div
          v-if="isNotListedPartner"
          class="tw:mt-6 tw:gap-x-2 tw:shadow-lg tw:rounded-xl tw:p-2 tw:flex tw:md:flex-row tw:flex-col tw:justify-between tw:gap-y-4"
        >
          <div class="tw:px-2 tw:border-2 tw:border-blue-700 tw:w-full tw:rounded-lg tw:p-1 tw:flex tw:flex-col">
            <label class="tw:text-gray-600 tw:text-xs tw:mb-0">
              {{ $t('rc_menu_booking_engine_selected') }}
              <input
                v-model="userTypedPartner"
                autofocus
                :disabled="disabledSendButton"
                :placeholder="$t('rc_menu_booking_engine_name_prompt')"
                class="tw:w-full tw:outline-none tw:border-none tw:font-bold tw:text-sm tw:text-gray-800 tw:placeholder:text-gray-800 tw:placeholder:font-bold"
                data-testid="readmore-custom-partner-input"
              />
            </label>
          </div>
          <MainButton
            dataTestId="readmore-save-engine-button"
            @click="sendCustomPartner"
            :disabled="disabledSendButton || userTypedPartner === ''"
            ><span class="tw:text-nowrap">{{ $t('rc_booking_engine_name_save') }}</span></MainButton
          >
        </div>

        <div v-if="isNoBookingEngine" class="tw:mx-auto tw:w-full tw:md:w-auto tw:mt-8">
          <MainLinkButton dataTestId="readmore-check-list-button" class="tw:w-full" @click="openPartnersList">
            <span class="tw:text-nowrap"> {{ $t('rc_connectivity_providers_cta') }}</span>
          </MainLinkButton>
        </div>
      </div>

      <div
        class="tw:bottom-0 tw:border-t tw:border-gray-300 tw:px-6 tw:py-4 tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:gap-y-4"
      >
        <GhostButton
          class="tw:flex tw:items-center tw:gap-x-3 tw:justify-center"
          dataTestId="readmore-back-button"
          @click="backAction"
        >
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 1L1 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <path d="M6 11L1 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          {{ $t('back_cta') }}
        </GhostButton>

        <SecondaryLinkButton
          :href="contactUsURL"
          class="icon-btn tw:gap-x-1 tw:items-center"
          dataTestId="readmore-contact-us-button"
          target="_blank"
          rel="noopener noreferrer"
        >
          <EditIcon />
          {{ $t('rc_contact_us_cta') }}</SecondaryLinkButton
        >
      </div>
    </div>
  </BaseCard>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import DOMPurify from 'dompurify'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import EditIcon from '@/components/icons/EditIcon.svg?component'
import PartnerIcon from '@/components/icons/PartnerIcon.svg?component'
import NotEligibleStepper from '@/components/rateConnect/notEligible/NotEligibleStepper.vue'
import SecondaryLinkButton from '@/components/base/buttons/SecondaryLinkButton.vue'
import MainLinkButton from '@/components/base/buttons/MainLinkButton.vue'

import { getContactUrl } from '@/utils/Utility.ts'
import { selectedAccommodationId } from '@/layouts/queries'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { useSessionValues } from '@/composables/useSessionValues'

import appAnalytics from '@/utils/tracking'
import useToastNotifications from '@/components/notifications/useToastNotifications'

const { displayNotification } = useToastNotifications()
const { language, userId } = useSessionValues()

const router = useRouter()
const selectedPartner = ref(null)
const isNoBookingEngine = ref(false)
const isNotListedPartner = ref(false)
const disabledSendButton = ref(false)
const userTypedPartner = ref('')

const { studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const isPaidClient = computed(() => Boolean(studioPlusCampaign.value))

onMounted(() => {
  const state = window.history.state
  if (state?.partner) {
    selectedPartner.value = DOMPurify.sanitize(state.partner)
  } else if (state?.value === 'no-booking-engine') {
    isNoBookingEngine.value = true
  } else if (state?.value === 'not-listed-partner') {
    isNotListedPartner.value = true
  }
})
const title = computed(() => {
  if (isNoBookingEngine.value || isNotListedPartner.value) return 'rc_ineligible_message_header'
  else return 'rc_eligible_message_header'
})

const subtitle = computed(() => {
  if (isNoBookingEngine.value) return 'rc_ineligble_message'
  else if (isNotListedPartner.value) return 'rc_ineligible_booking_engine_message'
  else return 'rc_eligible_message'
})
const backAction = () => {
  selectedPartner.value = null
  isNoBookingEngine.value = false
  isNotListedPartner.value = false
  disabledSendButton.value = false
  userTypedPartner.value = ''
  router.push({ name: 'rate-connect-not-eligible' })
}
const openEmailClient = () => {
  window.location.href = 'mailto:'
}

const contactUsURL = computed(() =>
  getContactUrl(language.value, userId.value, selectedAccommodationId.value, isPaidClient.value)
)

const PARTNERS_LIST_LANGUAGE_FALLBACKS = {
  'en-GB': 'en-US',
}

const partnersListUrl = computed(() => {
  const resolvedLanguage = PARTNERS_LIST_LANGUAGE_FALLBACKS[language.value] ?? language.value
  return `https://trivago.my.site.com/trivagoSupport/help?articleUrl=Connectivity-providers-that-work-with-trivago&language=${resolvedLanguage}`
})

const openPartnersList = () => {
  return window.open(partnersListUrl.value, '_blank')
}

const sendCustomPartner = () => {
  disabledSendButton.value = true
  appAnalytics.track(appAnalytics.events.RC_NOT_ELIGIBLE_CUSTOM_PARTNER, {
    item_id: selectedAccommodationId.value,
    custom_partner: userTypedPartner.value,
  })
  displayNotification({
    title: 'setting_updated_success',
    titleTranslationKey: true,
    message: 'rc_booking_engine_saved_success',
    isTranslationKey: true,
    type: 'success',
  })
}
</script>
<style scoped>
.icon-btn:hover svg {
  fill: black;
}
</style>
